import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NoticeBox } from "@/components/NoticeBox";
import { todayLocal, type YMD } from "./dates";
import { MIN_PAYMENT_YEAR, MAX_PAYMENT_YEAR, parsePaymentDate, defaultFirstPayment, ymdToISO, formatFullDate, formatMonthYear, paymentDate } from "./paymentDates";

/**
 * First payment date state shared by the loan calculators. The default is the 1st of next month, fixed when the page opens so it
 * cannot change under the user at midnight. An empty or invalid entry falls back to the default; the date only labels the schedule.
 * The date maths itself lives in paymentDates.ts (also used by the utility API).
 */
export function useFirstPaymentDate() {
  const [defaultIso] = useState(() => ymdToISO(defaultFirstPayment(todayLocal())));
  const [value, setValue] = useState(defaultIso);
  const parsed = parsePaymentDate(value);
  const first: YMD = useMemo(() => parsed ?? parsePaymentDate(defaultIso)!, [value, defaultIso]); // eslint-disable-line react-hooks/exhaustive-deps
  const message = value.trim() !== "" && !parsed ? `Pick a date between ${MIN_PAYMENT_YEAR} and ${MAX_PAYMENT_YEAR}. Showing dates from ${formatFullDate(first)} for now.` : "";
  return { value, setValue, first, message };
}

/**
 * Optional start date for projections (SIP, compound interest, 401(k), FIRE, 529, college cost). Empty by default, so a tool behaves
 * exactly as before until the user picks a date. `start` is null while the field is empty or not a valid date. Same field and the
 * same date maths as the loan calculators, only without a default date.
 */
export function useOptionalStartDate() {
  const [value, setValue] = useState("");
  const parsed = parsePaymentDate(value);
  const message = value.trim() !== "" && !parsed ? `Pick a date between ${MIN_PAYMENT_YEAR} and ${MAX_PAYMENT_YEAR}, or clear the field. Showing the plain results for now.` : "";
  return { value, setValue, start: parsed as YMD | null, message };
}

type DateFieldState = { value: string; setValue: (v: string) => void; message: string };

export function FirstPaymentDateField({ id, state, help, label = "First payment date", compact }: { id: string; state: DateFieldState; help: string; label?: string; compact?: boolean }) {
  return (
    <div>
      <Label htmlFor={id} className={compact ? "text-xs" : undefined}>{label}</Label>
      <Input id={id} type="date" aria-label={label} aria-describedby={`${id}-help`} min={`${MIN_PAYMENT_YEAR}-01-01`} max={`${MAX_PAYMENT_YEAR}-12-31`} aria-invalid={state.message ? true : undefined} value={state.value} onChange={(e) => state.setValue(e.target.value)} />
      <p id={`${id}-help`} className="text-xs text-muted-foreground mt-1">{help}</p>
      {state.message && <p role="alert" className="text-xs text-destructive mt-1">{state.message}</p>}
    </div>
  );
}

/** "Payoff" notice: month and year of the last payment, with the first and last payment dates. */
export function PayoffDateNotice({ first, payments, testId }: { first: YMD; payments: number; testId: string }) {
  if (payments < 1) return null;
  const last = paymentDate(first, payments - 1);
  return (
    <NoticeBox className="text-center" testId={testId}>
      <p className="text-xs text-muted-foreground">Payoff</p>
      <p className="text-lg font-bold" data-testid={`${testId}-date`}>{formatMonthYear(last)}</p>
      <p className="text-xs text-muted-foreground" data-testid={`${testId}-detail`}>First payment {formatFullDate(first)}. Last payment {formatFullDate(last)}.</p>
    </NoticeBox>
  );
}
